import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { useJigsaw } from '../../context/JigsawContext';
import ChevronLeft from '../../icons/ChevronLeft';
import { palette } from '../../theme/colors';
import Box from '../Box/Box';
import IconButton from '../IconButton/IconButton';
import ProgressBar from '../ProgressBar/ProgressBar';
import Stack from '../Stack/Stack';
import Text from '../Text/Text';

type Props = {
  /** What to display in the title slot of the nav bar */
  children?: ReactNode;

  /** What to display in the secondary slot */
  subtitle?: ReactNode;

  /** The right-side, secondary icon button */
  rightItem?: ReactNode;

  /** Whether to hide the back button */
  hideBackButton?: boolean;

  /** Override for the back button (defaults to history.goBack()) */
  onGoBack?: () => void;

  /** Shows a progress bar at the set percentage. Should be between 0 and 1 */
  progress?: number;
};

const StyledSubtitle = styled(Text)`
  color: ${palette.gray[500]};
`;

const StyledRightItem = styled.div`
  display: inline-block;
`;

const getTitles = (children?: ReactNode, subtitle?: ReactNode) => {
  return (
    <Stack>
      {subtitle && typeof subtitle === 'string' ? (
        <StyledSubtitle variant="body2" textAlign="center">
          {subtitle}
        </StyledSubtitle>
      ) : (
        subtitle
      )}
      {typeof children === 'string' ? (
        <Text variant="subtitle" textAlign="center">
          {children}
        </Text>
      ) : (
        children
      )}
    </Stack>
  );
};

const NavBar = ({
  children,
  subtitle,
  hideBackButton,
  onGoBack,
  progress,
  rightItem,
}: Props) => {
  const { history } = useJigsaw();

  const showProgress = typeof progress !== 'undefined';
  return (
    <Stack>
      <Box
        height="64px"
        alignItems="center"
        alignSelf="stretch"
        boxShadow={showProgress ? 'none' : 'sm'}
        justifyContent="center"
        px={[1, 6]}
        py={1}
        display="grid"
        gridTemplateColumns="1fr auto 1fr"
      >
        <Box
          display="flex"
          flex="1"
          alignItems="center"
          justifyContent="flex-start"
        >
          {!hideBackButton && (
            <IconButton onClick={onGoBack ?? history.goBack}>
              <ChevronLeft size="14px" />
            </IconButton>
          )}
        </Box>

        <Box display="flex" flex="1" justifyContent="center">
          {getTitles(children, subtitle)}
        </Box>

        <Box flex="1" textAlign="right">
          {rightItem && <StyledRightItem>{rightItem}</StyledRightItem>}
        </Box>
      </Box>
      {typeof progress !== 'undefined' && <ProgressBar progress={progress} />}
    </Stack>
  );
};

NavBar.displayName = 'NavBar';
export default NavBar;
